import React, { useEffect, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { Filters, KIND_ICONS, KIND_LABELS, METHOD_ICONS, METHOD_LABELS, PointKind, SOURCE_LABELS } from './sentinel';

type Option = { value: string; label: string; icon?: string };

const KINDS: Option[] = (Object.keys(KIND_LABELS) as PointKind[]).map(k => ({ value: k, label: KIND_LABELS[k], icon: KIND_ICONS[k] }));
const METHODS: Option[] = Object.entries(METHOD_LABELS).map(([value, label]) => ({ value, label, icon: METHOD_ICONS[value] }));
const SOURCES: Option[] = Object.entries(SOURCE_LABELS).map(([value, label]) => ({ value, label }));
const DIRECTIONS: Option[] = [
  { value: 'entry', label: 'Entradas', icon: 'enter-outline' },
  { value: 'exit', label: 'Salidas', icon: 'exit-outline' },
];
const RESULTS: Option[] = [
  { value: 'granted', label: 'Pasaron', icon: 'checkmark-circle-outline' },
  { value: 'denied', label: 'Negados', icon: 'close-circle-outline' },
];

type Key = 'kind' | 'direction' | 'result' | 'method' | 'source';

const MENUS: { key: Key; label: string; options: Option[] }[] = [
  { key: 'kind', label: 'Tipo', options: KINDS },
  { key: 'direction', label: 'Dirección', options: DIRECTIONS },
  { key: 'result', label: 'Resultado', options: RESULTS },
  { key: 'method', label: 'Método', options: METHODS },
  { key: 'source', label: 'Origen', options: SOURCES },
];

type Props = {
  filters: Filters;
  pointName?: string;
  count: number | null;
  onChange: (patch: Partial<Filters>) => void;
  onClear: () => void;
};

export default function FilterBar({ filters, pointName, count, onChange, onClear }: Props) {
  const [open, setOpen] = useState<Key | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(null); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const active = MENUS.some(m => filters[m.key]) || !!filters.access_point_id || !!filters.q || !!filters.flag;

  return (
    <div className="cc-filters" ref={ref}>
      {pointName && (
        <button className="cc-chip on" onClick={() => onChange({ access_point_id: undefined })}>
          <Ionicons name="location-outline" size={13} color="currentColor" />
          {pointName}
          <span className="x"><Ionicons name="close" size={12} color="currentColor" /></span>
        </button>
      )}

      {MENUS.map(menu => {
        const value = filters[menu.key];
        const current = menu.options.find(o => o.value === value);
        return (
          <div key={menu.key} style={{ position: 'relative' }}>
            <button
              className={`cc-chip${current ? ' on' : ''}`}
              onClick={() => (current ? onChange({ [menu.key]: undefined }) : setOpen(open === menu.key ? null : menu.key))}
            >
              {current?.icon && <Ionicons name={current.icon as any} size={13} color="currentColor" />}
              {current ? current.label : menu.label}
              <span className="x"><Ionicons name={current ? 'close' : 'chevron-down'} size={12} color="currentColor" /></span>
            </button>
            {open === menu.key && (
              <div className="cc-menu">
                {menu.options.map(o => (
                  <button key={o.value} onClick={() => { onChange({ [menu.key]: o.value }); setOpen(null); }}>
                    {o.icon && <Ionicons name={o.icon as any} size={15} color="currentColor" />}
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <button
        className={`cc-chip${filters.flag === 'delinquent' ? ' on' : ''}`}
        onClick={() => onChange({ flag: filters.flag === 'delinquent' ? undefined : 'delinquent' })}
      >
        <Ionicons name="alert-circle-outline" size={13} color="currentColor" />
        Morosos
      </button>

      {active && (
        <button className="cc-chip" onClick={onClear} style={{ borderStyle: 'dashed' }}>
          Limpiar
        </button>
      )}

      {count !== null && <span className="cc-count-pill cc-num">{count.toLocaleString('es-MX')} intentos</span>}
    </div>
  );
}
